import { gql } from '@apollo/client';

// Fragment for product data
export const PRODUCT_FRAGMENT = gql`
  fragment ProductData on Product {
    id
    databaseId
    productId
    name
    slug
    description
    shortDescription
    sku
    featured
    catalogVisibility
    menuOrder
    date
    modified
    type
    image {
      id
      sourceUrl
      altText
    }
    ... on SimpleProduct {
      status
      price
      regularPrice
      salePrice
      onSale
      stockStatus
      stockQuantity
      manageStock
      weight
      length
      width
      height
      attributes {
        nodes {
          id
          name
          options
          variation
          visible
          ... on GlobalProductAttribute {
            slug
          }
        }
      }
      galleryImages {
        nodes {
          id
          sourceUrl
          altText
          mediaDetails {
            width
            height
          }
        }
      }
      productCategories {
        nodes {
          id
          name
          slug
          description
          image {
            sourceUrl
            altText
          }
        }
      }
    }
    ... on VariableProduct {
      status
      price
      regularPrice
      salePrice
      onSale
      stockStatus
      manageStock
      productCategories {
        nodes {
          id
          name
          slug
          description
          image {
            sourceUrl
            altText
          }
        }
      }
    }
    ... on ExternalProduct {
      status
      price
      regularPrice
      salePrice
      onSale
      productCategories {
        nodes {
          id
          name
          slug
          description
          image {
            sourceUrl
            altText
          }
        }
      }
    }
    ... on GroupProduct {
      status
      price
      regularPrice
      salePrice
      onSale
      productCategories {
        nodes {
          id
          name
          slug
          description
          image {
            sourceUrl
            altText
          }
        }
      }
    }
    averageRating
    reviewCount
    totalSales
    image {
      id
      sourceUrl
      altText
      mediaDetails {
        width
        height
      }
    }
    productTags {
      nodes {
        id
        name
        slug
      }
    }
  }
`;

// Fragment for product category data
export const PRODUCT_CATEGORY_FRAGMENT = gql`
  fragment ProductCategoryData on ProductCategory {
    id
    databaseId
    name
    slug
    description
    count
    image {
      id
      sourceUrl
      altText
      mediaDetails {
        width
        height
      }
    }
    parent {
      node {
        id
        name
        slug
      }
    }
    children {
      nodes {
        id
        name
        slug
        count
      }
    }
  }
`;

// Query to get all products with pagination
// Note: where parameter type removed due to GraphQL schema compatibility
export const GET_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query GetProducts(
    $first: Int = 20
    $after: String
  ) {
    products(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ...ProductData
      }
    }
  }
`;

// Query to get a single product by slug
export const GET_PRODUCT_BY_SLUG = gql`
  ${PRODUCT_FRAGMENT}
  query GetProductBySlug($slug: ID!) {
    product(id: $slug, idType: SLUG) {
      ... on SimpleProduct {
        ...ProductData
      }
      ... on VariableProduct {
        ...ProductData
      }
      ... on Product {
        reviews {
          nodes {
            id
            content
            rating
            date
            author {
              node {
                name
                avatar {
                  url
                }
              }
            }
          }
        }
        related {
          nodes {
            ... on SimpleProduct {
              ...ProductData
            }
            ... on VariableProduct {
              ...ProductData
            }
          }
        }
        upsell {
          nodes {
            ... on SimpleProduct {
              ...ProductData
            }
            ... on VariableProduct {
              ...ProductData
            }
          }
        }
        crossSell {
          nodes {
            ... on SimpleProduct {
              ...ProductData
            }
            ... on VariableProduct {
              ...ProductData
            }
          }
        }
      }
    }
  }
`;

// Query to get featured products
export const GET_FEATURED_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query GetFeaturedProducts($first: Int = 8) {
    products(first: $first, where: { featured: true, orderby: { field: DATE, order: DESC } }) {
      nodes {
        ... on SimpleProduct {
          ...ProductData
        }
        ... on VariableProduct {
          ...ProductData
        }
      }
    }
  }
`;

// Query to get products on sale
export const GET_SALE_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query GetSaleProducts($first: Int = 12) {
    products(first: $first, where: { onSale: true, orderby: { field: DATE, order: DESC } }) {
      nodes {
        ... on SimpleProduct {
          ...ProductData
        }
        ... on VariableProduct {
          ...ProductData
        }
      }
    }
  }
`;

// Query to get products by category
export const GET_PRODUCTS_BY_CATEGORY = gql`
  ${PRODUCT_FRAGMENT}
  query GetProductsByCategory(
    $categorySlug: String!
    $first: Int = 20
    $after: String
  ) {
    products(
      first: $first
      after: $after
      where: {
        categoryIn: [$categorySlug]
        orderby: { field: MENU_ORDER, order: ASC }
      }
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ... on SimpleProduct {
          ...ProductData
        }
        ... on VariableProduct {
          ...ProductData
        }
      }
    }
  }
`;

// Query to search products
export const SEARCH_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query SearchProducts(
    $search: String!
    $first: Int = 20
    $after: String
  ) {
    products(
      first: $first
      after: $after
      where: {
        search: $search
        orderby: { field: RELEVANCE, order: DESC }
      }
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ... on SimpleProduct {
          ...ProductData
        }
        ... on VariableProduct {
          ...ProductData
        }
      }
    }
  }
`;

// Newest products — sorted by creation date, most-recent first.
// Used by the homepage "New Arrivals" section and the /products?sort=Newest nav link.
export const GET_NEW_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query GetNewProducts($first: Int = 8) {
    products(first: $first, where: { orderby: { field: DATE, order: DESC } }) {
      nodes {
        ... on SimpleProduct {
          ...ProductData
        }
        ... on VariableProduct {
          ...ProductData
        }
      }
    }
  }
`;

// Query to get all product categories
export const GET_PRODUCT_CATEGORIES = gql`
  ${PRODUCT_CATEGORY_FRAGMENT}
  query GetProductCategories($first: Int = 50) {
    productCategories(first: $first, where: { hideEmpty: false }) {
      nodes {
        ...ProductCategoryData
      }
    }
  }
`;

// Query to get a single category by slug
export const GET_CATEGORY_BY_SLUG = gql`
  ${PRODUCT_CATEGORY_FRAGMENT}
  query GetCategoryBySlug($slug: ID!) {
    productCategory(id: $slug, idType: SLUG) {
      ...ProductCategoryData
    }
  }
`;

// Query to get cart contents (requires WooCommerce JWT or session)
export const GET_CART = gql`
  query GetCart {
    cart {
      contents {
        nodes {
          key
          product {
            node {
              id
              productId
              name
              slug
              image {
                sourceUrl
                altText
              }
              ... on SimpleProduct {
                price
                regularPrice
                salePrice
              }
            }
          }
          variation {
            node {
              id
              name
              price
              regularPrice
              salePrice
              image {
                sourceUrl
                altText
              }
            }
          }
          quantity
          total
          subtotal
        }
      }
      subtotal
      subtotalTax
      shippingTotal
      shippingTax
      feesTotal
      feesTax
      discountsTotal
      discountsTax
      total
      totalTax
      isEmpty
      needsShippingAddress
    }
  }
`;

// Query to get customer data
export const GET_CUSTOMER = gql`
  query GetCustomer {
    customer {
      id
      databaseId
      username
      email
      firstName
      lastName
      displayName
      billing {
        firstName
        lastName
        company
        address1
        address2
        city
        state
        postcode
        country
        email
        phone
      }
      shipping {
        firstName
        lastName
        company
        address1
        address2
        city
        state
        postcode
        country
      }
      orders {
        nodes {
          id
          databaseId
          orderKey
          status
          date
          total
          lineItems {
            nodes {
              product {
                node {
                  name
                  image {
                    sourceUrl
                    altText
                  }
                }
              }
              quantity
              total
            }
          }
        }
      }
    }
  }
`;

// Mutation to add product to cart
export const ADD_TO_CART = gql`
  mutation AddToCart($productId: Int!, $quantity: Int = 1, $variationId: Int) {
    addToCart(
      input: {
        productId: $productId
        quantity: $quantity
        variationId: $variationId
      }
    ) {
      cartItem {
        key
        product {
          node {
            id
            name
          }
        }
        quantity
        total
      }
      cart {
        contents {
          nodes {
            key
            quantity
            total
          }
        }
        total
      }
    }
  }
`;

// Mutation to update cart item quantity
export const UPDATE_CART_ITEM = gql`
  mutation UpdateCartItem($key: ID!, $quantity: Int!) {
    updateItemQuantities(
      input: {
        items: [{ key: $key, quantity: $quantity }]
      }
    ) {
      updated {
        key
        quantity
        total
      }
      cart {
        contents {
          nodes {
            key
            quantity
            total
          }
        }
        total
      }
    }
  }
`;

// Mutation to remove item from cart
export const REMOVE_FROM_CART = gql`
  mutation RemoveFromCart($key: ID!) {
    removeItemsFromCart(input: { keys: [$key] }) {
      removed {
        key
      }
      cart {
        contents {
          nodes {
            key
            quantity
            total
          }
        }
        total
      }
    }
  }
`;

// Mutation to clear cart
export const CLEAR_CART = gql`
  mutation ClearCart {
    emptyCart(input: {}) {
      cart {
        contents {
          nodes {
            key
          }
        }
      }
    }
  }
`;

// Query to get payment gateways
export const GET_PAYMENT_GATEWAYS = gql`
  query GetPaymentGateways {
    paymentGateways {
      nodes {
        id
        title
        description
        enabled
        methodTitle
        methodDescription
        icon
      }
    }
  }
`;

// Query to get shipping methods
export const GET_SHIPPING_METHODS = gql`
  query GetShippingMethods {
    shippingMethods {
      nodes {
        id
        title
        description
        enabled
      }
    }
  }
`;

// Query for homepage content
export const GET_HOMEPAGE_DATA = gql`
  query GetHomepageData {
    # Featured products — user tags products with "featured" in wp-admin
    featuredProducts: products(first: 8, where: { tagIn: ["featured"] }) {
      nodes {
        ... on SimpleProduct {
          id
          productId
          name
          slug
          description
          shortDescription
          price
          regularPrice
          salePrice
          onSale
          stockStatus
          averageRating
          reviewCount
          totalSales
          image {
            sourceUrl
            altText
          }
          productCategories {
            nodes {
              id
              name
              slug
            }
          }
        }
      }
    }

    # Sale / Limited Time Offer products — user tags with "sale" or "limited-time-offer"
    saleProducts: products(first: 6, where: { tagIn: ["sale", "limited-time-offer"] }) {
      nodes {
        ... on SimpleProduct {
          id
          productId
          name
          slug
          description
          shortDescription
          price
          regularPrice
          salePrice
          onSale
          stockStatus
          averageRating
          reviewCount
          totalSales
          image {
            sourceUrl
            altText
          }
          productCategories {
            nodes {
              id
              name
              slug
            }
          }
        }
      }
    }

    # New arrivals
    newProducts: products(first: 8, where: { orderby: { field: DATE, order: DESC } }) {
      nodes {
        ... on SimpleProduct {
          id
          productId
          name
          slug
          description
          shortDescription
          price
          regularPrice
          salePrice
          onSale
          stockStatus
          averageRating
          reviewCount
          totalSales
          image {
            sourceUrl
            altText
          }
          productCategories {
            nodes {
              id
              name
              slug
            }
          }
        }
      }
    }

    # Product categories (include empty so seeded categories show before products are added)
    productCategories(first: 20, where: { hideEmpty: false }) {
      nodes {
        id
        name
        slug
        count
        image {
          sourceUrl
          altText
        }
      }
    }
  }
`;

export default {
  GET_PRODUCTS,
  GET_PRODUCT_BY_SLUG,
  GET_FEATURED_PRODUCTS,
  GET_SALE_PRODUCTS,
  GET_PRODUCTS_BY_CATEGORY,
  SEARCH_PRODUCTS,
  GET_PRODUCT_CATEGORIES,
  GET_CATEGORY_BY_SLUG,
  GET_CART,
  GET_CUSTOMER,
  ADD_TO_CART,
  UPDATE_CART_ITEM,
  REMOVE_FROM_CART,
  CLEAR_CART,
  GET_PAYMENT_GATEWAYS,
  GET_SHIPPING_METHODS,
  GET_HOMEPAGE_DATA,
};